import React, { Component, useState, useEffect } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import CustomerSearch from 'components/site/customer-search-alt';

import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import Container from 'components/v2/container';
import {
  getReasonCodesByShipper,
  createReasonCodeCategory,
  updateReasonCodeCategory,
  getDefaultReasonCodes,
} from 'utils/ReasonCodesAPI';
import Label from 'components/v2/forms/Label';
import DeleteButton from '../../v2/buttons/DeleteButton';
import Checkbox from '../../v2/forms/Checkbox';

const default_categories = [
  {
    name: 'Cancellation',
    value: 'shipment_cancellation',
    localizationSlug: 'CANCELLATION',
  },
  {
    name: 'Stop Window Change',
    value: 'stop_window_change',
    localizationSlug: 'STOP_WINDOW_CHANGED',
  },
  {
    name: 'Stop Completion',
    value: 'stop_complete',
    localizationSlug: 'STOP_COMPLETION',
  },
  {
    name: 'Stop Arrival',
    value: 'stop_arrival',
    localizationSlug: 'STOP_ARRIVAL',
  },
  {
    name: 'Accessorials',
    value: 'accessorials',
    localizationSlug: 'ACCESSORIAL',
    count: 2,
  },
  {
    name: 'Credit Memo',
    value: 'credit_memo',
    localizationSlug: 'CREDIT_MEMO',
  },
];

const getDefaultShipperPayload = (shipper, type) => {
  return {
    lineOfBusiness: type,
    shipper: shipper._id,
    categories: [
      {
        name: 'stop_complete',
        subCategories: [],
      },
      {
        name: 'stop_window_change',
        subCategories: [],
      },
      {
        name: 'stop_arrival',
        subCategories: [],
      },
      {
        name: 'shipment_cancellation',
        subCategories: [],
      },
    ],
  };
};

const ReasonCodeRow = ({
  rowInfo,
  type,
  handleCreateReasonCode,
  handleUpdateReasonCode,
  handleDeleteReasonCode,
  index,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(type === 'update' ? rowInfo.code : '');
  const [description, setDescription] = useState(
    type === 'update' ? rowInfo.description : '',
  );
  const error = false;

  useEffect(() => {
    if (rowInfo) {
      setCode(rowInfo.code);
      setDescription(rowInfo.description);
    }
  }, [rowInfo]);

  const handleReasonCodeChange = (value) => {
    setCode(value);
  };
  const handleReasonCodeDescriptionChange = (value) => setDescription(value);

  const handleCreateButton = () => async () => {
    const payload = {
      code,
      description,
    };
    handleCreateReasonCode([payload]);
    setCode('');
    setDescription('');
  };

  const handleUpdateButton = () => async () => {
    const payload = {
      code,
      description,
    };
    handleUpdateReasonCode(index, payload);
  };

  const handleDeleteButton = () => async () => {
    handleDeleteReasonCode(index);
  };

  return (
    <div data-testid="div-1946a3c9" className="p-2 flex flex-row justify-start">
      <CargomaticInput
        dataTestId="CargomaticInput-bd73d370"
        className="w-12 mr-8"
        label={t('CODE')}
        type="text"
        error={error}
        info={t('REASON_CHARACTER_CODE_INFO')}
        value={code}
        onChange={handleReasonCodeChange}
        disabled={false}
      />
      <CargomaticInput
        dataTestId="CargomaticInput-c1e1be84"
        className="w-96 mr-8"
        label={t('DESCRIPTION', { count: 1 })}
        type="text"
        info={t('HUMAN_READABLE_REASON_CODE ')}
        value={description}
        onChange={handleReasonCodeDescriptionChange}
        disabled={false}
      />
      <div data-testid="div-45ef7b2c" className="mt-6 ml-2 flex flex-row">
        {type === 'update' ? (
          <div data-testid="div-58e893bb" className="flex flex-row">
            <button
              data-testid="button-f5360be2"
              className="button button-blue h-10 mr-2"
              disabled={error}
              onClick={handleUpdateButton()}
            >
              {t('UPDATE')}
            </button>
            <DeleteButton
              dataTestId="DeleteButton-73314fcb"
              onClick={handleDeleteButton()}
            />
          </div>
        ) : (
          <button
            data-testid="button-2545bf20"
            className="button button-green h-10 mr-2"
            onClick={handleCreateButton()}
          >
            {t('CREATE')}
          </button>
        )}
      </div>
      <div data-testid="div-9cef1cfd" className="mt-8 ml-4">
        {error && (
          <span
            data-testid="span-959510b3"
            className="text-red-500 italic mt-4"
          >
            {t('ERROR_REASON_CODE_EXISTS')}
          </span>
        )}
      </div>
    </div>
  );
};

// Form to create a reason code category
const CreateReasonCodeCategoryForm = ({ addReasonCodeSubCategory }) => {
  const { t } = useTranslation();
  const [reasonType, setReasonType] = useState(''); // Carrier Service Failure
  const [commentRequired, setCommentRequired] = useState(false);

  const handleReasonCodeTypeChange = (value) => setReasonType(value);
  const handleCommentRequiredCheckbox = (value) => setCommentRequired(value);

  const handleCreateButton = () => async () => {
    const subCategory = {
      reasonType,
      commentRequired,
      reasonCodes: [],
    };
    addReasonCodeSubCategory(subCategory);
  };

  const checkFields = () => {
    if (!reasonType || !reasonType.length) {
      return true;
    }
    return false;
  };

  return (
    <div data-testid="div-21fa920c" className="w-full h-24 flex">
      <div
        data-testid="div-f35991e9"
        className="w-full flex flex-row justify-start"
      >
        <CargomaticInput
          dataTestId="CargomaticInput-f954107d"
          className="w-72 mr-8"
          label={t('REASON_CODE_TYPE')}
          type="text"
          info={t('REASON_CODE_TYPE')}
          value={reasonType}
          onChange={handleReasonCodeTypeChange}
        />

        <Checkbox
          dataTestId="Checkbox-8fc018bc"
          className="mt-8"
          checked={commentRequired}
          id="comment"
          onChange={handleCommentRequiredCheckbox}
        >
          {t('COMMENT_REQUIRED')}
        </Checkbox>

        <button
          data-testid="button-84d71144"
          disabled={checkFields()}
          className="button button-green h-10 ml-8 mt-4"
          onClick={handleCreateButton()}
        >
          {t('NEW_SUB_CATEGORY')}
        </button>
      </div>
    </div>
  );
};

// Form to update a reason code category
const UpdateReasonCodeCategoryForm = ({
  item,
  index,
  updateReasonCodeSubCategory,
  deleteReasonCodeSubCategory,
}) => {
  const { t } = useTranslation();
  const [reasonType, setReasonType] = useState(item.reasonType);
  const [commentRequired, setCommentRequired] = useState(item.commentRequired);
  const [reasonCodes, setReasonCodes] = useState(item.reasonCodes || []);
  const id = item._id;

  useEffect(() => {
    setReasonCodes(item.reasonCodes);
  }, [item]);

  const handleReasonCodeTypeChange = (value) => setReasonType(value);
  const handleCommentRequiredCheckbox = (value) => setCommentRequired(value);

  const handleDeleteButton = () => async () => {
    deleteReasonCodeSubCategory(index);
  };

  const checkFields = () => {
    if (!reasonType || !reasonType.length) {
      return false;
    }
    return true;
  };

  const handleUpdateButton = () => async () => {
    const isValid = checkFields();
    if (!isValid) {
      return null;
    }

    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: item.reasonCodes || [],
    };

    updateReasonCodeSubCategory(index, data);
  };

  const handleCreateReasonCode = (payload) => {
    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: [...reasonCodes, ...payload],
    };

    updateReasonCodeSubCategory(index, data);
  };

  const handleUpdateReasonCode = (codeIndex, payload) => {
    const newCodes = [...reasonCodes];
    newCodes[codeIndex] = payload;
    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: newCodes,
    };

    updateReasonCodeSubCategory(index, data);
  };

  const handleDeleteReasonCode = (codeIndex) => {
    let newCodes = [...reasonCodes];
    newCodes.splice(codeIndex, 1);

    const data = {
      reasonType,
      commentRequired,
      _id: id,
      reasonCodes: newCodes,
    };
    updateReasonCodeSubCategory(index, data);
  };

  return (
    <div
      data-testid="div-3eeef37f"
      className="w-full flex flex-col border-black mt-8"
    >
      <div
        data-testid="div-dfb19777"
        className="w-full flex flex-row justify-start"
      >
        <CargomaticInput
          dataTestId="CargomaticInput-e1d5b452"
          className="w-72 mr-8"
          label={t('REASON_CODE_TYPE')}
          type="text"
          info={t('REASON_CODE_TYPE')}
          value={reasonType}
          onChange={handleReasonCodeTypeChange}
          disabled={false}
        />
        <Checkbox
          dataTestId="Checkbox-5f2a30a1"
          className="mt-8"
          checked={commentRequired}
          id="comment"
          onChange={handleCommentRequiredCheckbox}
        >
          {t('COMMENT_REQUIRED')}
        </Checkbox>
        <div
          data-testid="div-fccdef1c"
          className="mt-6 ml-8 w-48 flex flex-row"
        >
          <button
            data-testid="button-cede8d06"
            className="button button-blue h-10 mr-2"
            onClick={handleUpdateButton()}
          >
            {t('UPDATE')}
          </button>
          <DeleteButton
            dataTestId="DeleteButton-0bd120f5"
            onClick={handleDeleteButton()}
          />
        </div>
      </div>
      <div
        data-testid="div-ece0d2f5"
        className="w-full flex flex-col mt-4 ml-8"
      >
        <p data-testid="p-e2714430" className="p-2 text-grey-darker">
          {t('CURRENT_REASON_CODES')}:
        </p>
        {reasonCodes &&
          reasonCodes.map((rc, index) => (
            <ReasonCodeRow
              key={index}
              reasonCodeRow={item}
              rowInfo={rc}
              type="update"
              index={index}
              handleUpdateReasonCode={handleUpdateReasonCode}
              handleDeleteReasonCode={handleDeleteReasonCode}
            />
          ))}

        <ReasonCodeRow
          reasonCodeRow={item}
          type="create"
          handleCreateReasonCode={handleCreateReasonCode}
        />
      </div>
      <hr data-testid="hr-3a59c58c" />
    </div>
  );
};

const getReasonCodeForm = (type) => {
  switch (type) {
    case 'default':
      return <DefaultReasonCodeForm />;
    case 'custom':
      return <ShipperReasonCodeForm />;
    default:
      return <DefaultReasonCodeForm />;
  }
};

class ReasonCodes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: 'default',
    };
  }

  render() {
    const { t } = this.props;
    const { type } = this.state;
    const style =
      'text-gray-600 py-4 px-6 block hover:text-blue-500 focus:outline-none';
    const activeStyle =
      'text-gray-600 py-4 px-6 block hover:text-blue-500 focus:outline-none text-blue-500 border-b-2 font-medium border-blue-500';

    return (
      <Container dataTestId="Container-89d74fd3" className="py-8">
        <div data-testid="div-4c0c2970" className="flex flex-col">
          <div
            data-testid="div-6f10b9fc"
            className="flex flex-col p-4 mr-8 header"
          >
            <div data-testid="div-737e3e29" className="flex flex-row w-full">
              {/* <h3 className="mt-0 mb-4 mr-2" style={{ lineHeight: '36px' }}>
                Reason Codes
              </h3> */}
              <div data-testid="div-093bb0f2" className="mb-8">
                <nav
                  data-testid="nav-c7192553"
                  className="flex flex-col sm:flex-row"
                >
                  <button
                    data-testid="button-3ea9d808"
                    onClick={() => this.setState({ type: 'default' })}
                    className={type === 'default' ? activeStyle : style}
                  >
                    {t('DEFAULT_REASON_CODES')}
                  </button>
                  <button
                    data-testid="button-b5e4558b"
                    onClick={() => this.setState({ type: 'custom' })}
                    className={type === 'custom' ? activeStyle : style}
                  >
                    {t('SHIPPER_REASON_CODES')}
                  </button>
                </nav>
              </div>
            </div>

            <div data-testid="div-a045ec2a">
              {getReasonCodeForm(this.state.type)}
            </div>
          </div>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(ReasonCodes);

const DefaultReasonCodeForm = withTranslation()(
  class DefaultReasonCodeForm extends Component {
    constructor(props) {
      super(props);

      this.state = {
        type: 'shorthaul',
        category: 'stop_complete',
        default_shorthaul: {},
        default_drayage: {},
        shipper: null,
        shipperInput: '',
      };
    }

    async componentDidMount() {
      await this.getReasonCodes();
    }

    /* Reason Code Categories
    - stop_complete (Stop Complete Modal)
    - stop_window_change (Add/Edit Appointment Modal)
    - shipment_cancellation (Cancel Shipment Modal)
  */

    getReasonCodes = async () => {
      try {
        const items = await getDefaultReasonCodes();
        if (!items.length) {
          return null;
        }

        const default_drayage = items.find(
          (i) => i.lineOfBusiness === 'drayage',
        );
        const default_shorthaul = items.find(
          (i) => i.lineOfBusiness === 'shorthaul',
        );

        this.setState({ default_drayage, default_shorthaul });
      } catch (e) {
        console.error('Error getting reason codes', e);
      }
    };

    getCodes = (type) => {
      const { default_drayage, default_shorthaul } = this.state;
      switch (type) {
        case 'shorthaul':
          return default_shorthaul;
        case 'drayage':
          return default_drayage;
        default:
          return {};
      }
    };

    handleCategorySelect = (item) => {
      this.setState({ category: item.value });
    };

    handleShipmentTypeSelect = (item) => {
      const codes = this.getCodes(item.value);
      const newCategory = codes?.categories?.[0]?.name || '';
      this.setState({ type: item.value, category: newCategory });
    };

    handleAddReasonCodeSubCategory = (subCategory) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.push(subCategory);
        }
        return c;
      });

      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleUpdateSubCategory = (index, payload) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories[index] = payload;
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    deleteReasonCodeSubCategory = (index) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.splice(index, 1);
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleSave = async (codes, category) => {
      const res = await updateReasonCodeCategory(codes._id, codes);
      if (category === 'shorthaul') {
        this.setState({ default_shorthaul: res });
      } else {
        this.setState({ default_drayage: res });
      }
    };

    renderShipperFilter() {
      const { t } = this.props;
      return (
        <fieldset data-testid="fieldset-c7f154ae">
          <div data-testid="div-72b4960d" className="mb-4 customer-search">
            <CustomerSearch
              clearOnSelect
              onChange={this.handleShipperFieldChange}
              onSelect={this.handleShipperSelect}
              value={this.state.shipperInput}
              placeholder={t('SEARCH_FOR_SHIPPER')}
              type="shippers"
            />
          </div>
        </fieldset>
      );
    }

    renderReasonCodeCategory() {
      const { t } = this.props;
      const { category, type } = this.state;
      const codes = this.getCodes(type);
      if (!codes || !codes.categories) {
        return null;
      }

      const reason_codes = codes.categories.find((c) => c.name === category);

      if (!reason_codes) {
        return (
          <span data-testid="span-8d759f0b">
            {t('UNABLE_TO_FIND_DEFAULT_REASON_CODE')}
          </span>
        );
      }

      return (
        <div data-testid="div-2d4bfe94">
          {reason_codes.subCategories.map((rc, index) => {
            return (
              <UpdateReasonCodeCategoryForm
                item={rc}
                key={index}
                index={index}
                updateReasonCodeSubCategory={this.handleUpdateSubCategory}
                getReasonCodes={this.getDefaultReasonCodes}
                deleteReasonCodeSubCategory={this.deleteReasonCodeSubCategory}
              />
            );
          })}
        </div>
      );
    }

    render() {
      const { category, type, shipper } = this.state;
      const { t } = this.props;
      const CATEGORY_UPPERCASE = category?.toUpperCase() || '';
      const reasonCodesTitle = t(`${CATEGORY_UPPERCASE}_REASON_CODES`);
      const codes = this.getCodes(type);
      const categoryItems = codes?.categories?.map((item) => ({
        value: item.name,
        localizationSlug: item.name.toUpperCase(),
      }));
      if (this.props.usingShipper && !shipper) {
        return (
          <div data-testid="div-10b306e3" className="mt-6 mr-8 ml-6 w-64">
            {this.renderShipperFilter()}
          </div>
        );
      }

      return (
        <div data-testid="div-09e67a0d" className="relative">
          <div data-testid="div-53e292af" className="flex flex-row">
            {this.props.usingShipper && (
              <div data-testid="div-6a90edfe" className="mt-6 mr-8">
                {this.renderShipperFilter()}
              </div>
            )}

            <div data-testid="div-790928c1" className="mb-8">
              <CargomaticSelect
                dataTestId="CargomaticSelect-a7896f82"
                className="w-48 mr-8"
                label={t('SHIPMENT_TYPE')}
                info={t('PLEASE_SELECT_SHIPMENT_TYPE')}
                caption={t('SELECT_SHIPMENT_TYPE')}
                items={[
                  {
                    name: 'shorthaul',
                    value: 'shorthaul',
                    localizationSlug: 'SHORTHAUL',
                  },
                  {
                    name: 'drayage',
                    value: 'drayage',
                    localizationSlug: 'DRAYAGE',
                  },
                ]}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={type}
                onSelect={this.handleShipmentTypeSelect}
              />
            </div>

            <div data-testid="div-98e6d6b0" className="mb-8">
              <CargomaticSelect
                dataTestId="CargomaticSelect-86224326"
                className="w-48 mr-8"
                label={t('CATEGORY')}
                info={t('PLEASE_SELECT_REASON_CODE_CATEGORY')}
                caption={t('SELECT_A_CATEGORY')}
                items={categoryItems}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={category}
                onSelect={this.handleCategorySelect}
              />
            </div>
          </div>
          <hr data-testid="hr-73e1bbba" />
          {category && type && (
            <div
              data-testid="div-f6da6962"
              className=""
              key={`code_category_form_${category}}`}
            >
              <CreateReasonCodeCategoryForm
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p
                data-testid="p-72937a02"
                className="text-grey-darker bolder text-lg mb-6"
              >
                {t(reasonCodesTitle)}
              </p>
              {this.renderReasonCodeCategory(category)}
            </div>
          )}
        </div>
      );
    }
  },
);

const ShipperReasonCodeForm = withTranslation()(
  class ShipperReasonCodeForm extends Component {
    constructor(props) {
      super(props);

      this.state = {
        type: 'shorthaul',
        category: 'stop_complete',
        default_shorthaul: {},
        default_drayage: {},
        shipper: null,
        shipperInput: '',
        missing_codes: false,
      };
    }

    handleShipperFieldChange = (value) => {
      this.setState({ shipperInput: value });
    };

    handleShipperSelect = async (_value, item) => {
      try {
        const items = await getReasonCodesByShipper(item._id);
        if (!items || !items.length) {
          return this.setState({ shipper: item, missing_codes: true });
        }
        const default_drayage = items.find(
          (i) => i.lineOfBusiness === 'drayage',
        );
        const default_shorthaul = items.find(
          (i) => i.lineOfBusiness === 'shorthaul',
        );

        this.setState({ shipper: item, default_drayage, default_shorthaul });
      } catch (e) {
        console.error('Error getting reason codes', e);
      }
    };

    // Initialize Shipper Reason Codes
    handleCreateReasonCodesForShipper = async () => {
      const { shipper } = this.state;

      const default_shorthaul = await createReasonCodeCategory(
        getDefaultShipperPayload(shipper, 'shorthaul'),
      );
      const default_drayage = await createReasonCodeCategory(
        getDefaultShipperPayload(shipper, 'drayage'),
      );
      this.setState({
        missing_codes: false,
        default_drayage,
        default_shorthaul,
      });
    };

    handleCategorySelect = (item) => {
      this.setState({ category: item.value });
    };

    handleShipmentTypeSelect = (item) => {
      this.setState({ type: item.value });
    };

    handleAddReasonCodeSubCategory = (subCategory) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.push(subCategory);
        }
        return c;
      });

      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleUpdateSubCategory = (index, payload) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories[index] = payload;
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    deleteReasonCodeSubCategory = (index) => {
      const { type, category, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;

      const reasonCodes = codes.categories.map((c) => {
        if (c.name === category) {
          c.subCategories.splice(index, 1);
        }
        return c;
      });
      codes.categories = reasonCodes;
      this.handleSave(codes, category);
    };

    handleSave = async (codes, category) => {
      const items = await updateReasonCodeCategory(codes._id, codes);

      if (category === 'shorthaul') {
        this.setState({ default_shorthaul: items });
      } else {
        this.setState({ default_drayage: items });
      }
    };

    renderShipperFilter() {
      const { t } = this.props;
      return (
        <fieldset data-testid="fieldset-8d7bc82a">
          <div data-testid="div-48d32dc9" className="mb-4 mt-1 customer-search">
            <Label
              dataTestId="Label-5873ba9d"
              helptext={t('CURRENT_SELECTED_SHIPPER')}
            >
              {t('SHIPPER')}:{' '}
              {this.state.shipper ? this.state.shipper.name : t('NONE')}
            </Label>
            <CustomerSearch
              clearOnSelect
              onChange={this.handleShipperFieldChange}
              onSelect={this.handleShipperSelect}
              value={this.state.shipperInput}
              placeholder={t('SEARCH_FOR_SHIPPER')}
              type="shippers"
            />
          </div>
        </fieldset>
      );
    }

    renderReasonCodeCategory() {
      const { category, type, default_drayage, default_shorthaul } = this.state;
      const codes = type === 'shorthaul' ? default_shorthaul : default_drayage;
      const { t } = this.props;
      if (!codes || !codes.categories) {
        return null;
      }

      const reason_codes = codes.categories.find((c) => c.name === category);

      if (!reason_codes) {
        return (
          <span data-testid="span-6172b29e">
            {t('UNABLE_TO_FIND_DEFAULT_REASON_CODE')}
          </span>
        );
      }

      return (
        <div data-testid="div-a7a42b4f">
          {reason_codes.subCategories.map((rc, index) => {
            return (
              <UpdateReasonCodeCategoryForm
                item={rc}
                key={index}
                index={index}
                updateReasonCodeSubCategory={this.handleUpdateSubCategory}
                getReasonCodes={this.getDefaultReasonCodes}
                deleteReasonCodeSubCategory={this.deleteReasonCodeSubCategory}
              />
            );
          })}
        </div>
      );
    }

    render() {
      const { category, type, shipper, missing_codes } = this.state;
      const { t } = this.props;

      if (!shipper) {
        return (
          <div data-testid="div-4889983b" className="mt-6 mr-8 ml-6 w-64">
            {this.renderShipperFilter()}
          </div>
        );
      }

      if (shipper && missing_codes) {
        return (
          <div data-testid="div-54458b9d" className="mt-6 mr-8 ml-6 w-64">
            {this.renderShipperFilter()}
            <button
              data-testid="button-187a225e"
              className="button button-green h-14 mt-6"
              onClick={this.handleCreateReasonCodesForShipper}
            >
              {t('CREATE_CUSTOM_REASON_CODE_FOR_SHIPPER')}
            </button>
          </div>
        );
      }

      return (
        <div data-testid="div-b2cf140d" className="relative">
          <div data-testid="div-07ac097a" className="flex flex-row">
            <div data-testid="div-76ff4aea" className="mr-8">
              {this.renderShipperFilter()}
            </div>

            <div data-testid="div-b1eb3478" className="mb-8">
              <CargomaticSelect
                dataTestId="CargomaticSelect-69794461"
                className="w-48 mr-8"
                label={t('SHIPMENT_TYPE')}
                info={t('PLEASE_SELECT_SHIPMENT_TYPE')}
                caption={t('SELECT_SHIPMENT_TYPE')}
                items={[
                  {
                    name: 'shorthaul',
                    value: 'shorthaul',
                    localizationSlug: 'SHORTHAUL',
                  },
                  {
                    name: 'drayage',
                    value: 'drayage',
                    localizationSlug: 'DRAYAGE',
                  },
                ]}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={type}
                onSelect={this.handleShipmentTypeSelect}
              />
            </div>

            <div data-testid="div-6411b25f" className="mb-8">
              <CargomaticSelect
                dataTestId="CargomaticSelect-1b448b8c"
                className="w-48 mr-8"
                label={t('CATEGORY')}
                info={t('PLEASE_SELECT_REASON_CODE_CATEGORY')}
                caption={t('PLEASE_SELECT_REASON_CODE_CATEGORY')}
                items={default_categories}
                showCaption
                valueKey="value"
                textKey="localizationSlug"
                value={category}
                onSelect={this.handleCategorySelect}
              />
            </div>
          </div>
          <hr data-testid="hr-1c0acce9" />
          {category === 'stop_window_change' && (
            <div data-testid="div-53a9becd" className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <span
                data-testid="span-cc73c505"
                className="text-grey-darker bolder text-lg mb-6"
              >
                {t('STOP_WINDOW_CHANGED')}
              </span>
              {this.renderReasonCodeCategory('stop_window_change')}
            </div>
          )}
          {category === 'shipment_cancellation' && (
            <div data-testid="div-39172821" className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p
                data-testid="p-3de884fd"
                className="text-grey-darker bolder text-lg mb-6"
              >
                {t('SHIPMENT_CANCELATION')}
              </p>
              {this.renderReasonCodeCategory('shipment_cancellation')}
            </div>
          )}
          {category === 'stop_complete' && (
            <div data-testid="div-445d8dfb" className="">
              <CreateReasonCodeCategoryForm
                getReasonCodes={this.getReasonCodes}
                addReasonCodeSubCategory={this.handleAddReasonCodeSubCategory}
              />
              <p
                data-testid="p-b23c7b8e"
                className="text-grey-darker bolder text-lg mb-6"
              >
                {t('STOP_COMPLETE_REASON_CODES')}
              </p>
              {this.renderReasonCodeCategory('stop_complete')}
            </div>
          )}
        </div>
      );
    }
  },
);
